<template>
    <div class="text-left">
        <p>{{ descriptionText }}</p>
        <div class="alert alert-success" v-if="copied">{{ copyConfirmationText }}</div>
        <div class="input-group mb-2">
            <input
                :aria-label="descriptionText"
                class="form-control input-monospace"
                data-autoselect
                :inputClass="inputClass"
                readonly type="text"
                :value="link"
            >
            <div class="input-group-btn">
                <button
                    class="btn"
                    :class="btnClass"
                    type="button"
                    v-clipboard:copy="link"
                    v-clipboard:success="onCopy"
                >
                    {{ $t('copy') }}
                </button>
            </div>
        </div>
        <p class="bold">{{ $t('friend_inviter.more_ways_share') }}</p>
        <social-sharing
            inline-template
            class="mb-2"
            :title="$t('friend_inviter.social_title')"
            :url="link"
        >
            <div class="row">
                <div class="col-md-4">
                    <network
                        class="btn btn-success btn-sm mb5 btn-block"
                        network="whatsapp"
                        tag="button"
                        type="button"
                    >
                        <i class="fab fa-whatsapp"></i>{{ $t('friend_inviter.whatsapp') }}
                    </network>
                </div>
                <div class="col-md-4 mr-auto">
                    <network
                        class="btn btn-success btn-sm mb5 btn-block"
                        network="sms"
                        tag="button"
                        type="button"
                    >
                        <i class="fas fa-sms"></i>{{ $t('friend_inviter.sms') }}
                    </network>
                </div>
                <div class="col-md-4 mr-auto">
                    <network
                        class="btn btn-success btn-sm mb5 btn-block"
                        network="email"
                        tag="button"
                        type="button"
                    >
                        <i class="fas fa-inbox"></i> {{ $t('friend_inviter.email_share') }}
                    </network>
                </div>


            </div>
        </social-sharing>
        <display-referral-benefits
            :referral-scheme="referralScheme"
            :referral-text="$t('friend_inviter.referral_text')"
            :referrer-text="$t('friend_inviter.referrer_text')"
            :user-referrer-code="userReferrerCode"
            v-if="referralScheme"
        ></display-referral-benefits>
    </div>

</template>

<script>
import {money} from '../Utility/filters/money';
import SocialSharing from 'vue-social-sharing';
import DisplayReferralBenefits from "../ReferralSchemes/DisplayBenefits.vue";

export default {
    name: "FriendInviter",

    components: {
        DisplayReferralBenefits,
        SocialSharing,
    },

    filters: {
        money,
    },


    props: {
        /**
         * The URL that will be shared when users copy/share
         */
        link: {
            type: String,
            required: true,
        },
        /**
         * Indicates which page is hosting the share component
         * Used to customize behavior based on context
         */
        hostPage: {
            type: String,
            required: false,
            default: 'landing-page',
            validator: function (value) {
                return ['landing-page', 'booking-confirmation', 'user-profile'].indexOf(value) !== -1
            }
        },
        /**
         * Configuration for the referral program
         * Set to null when no referral program is active
         */
        referralScheme: {
            type: Object,
            required: false,
            default: null,
        },
        /**
         * Controls the size of buttons and inputs
         * Possible values: 'sm', 'md', 'lg'
         */
        size: {
            type: String,
            required: false,
            default: 'md',
            validator: function (value) {
                return ['sm', 'md', 'lg'].indexOf(value) !== -1
            }
        },
        /**
         * The current user's referrer code to be included in shared links
         */
        userReferrerCode: {
            type: String,
            required: true,
        },

    },
    computed: {
        btnClass() {
            return "btn-" + this.size;
        },
        inputClass() {
            return "input-" + this.size;
        },
        referredCreditIsPercentage() {
            return this.referralScheme && this.referralScheme.referred_credit_type === 'percentage';
        },
        descriptionText() {
            // For user-profile page
            if (this.hostPage === 'user-profile') {
                if (!this.referralScheme) {
                    return this.$t('friend_inviter.share_discovery');
                }

                let baseText;
                if (this.referredCreditIsPercentage) {
                    const percentage = this.referralScheme.referred_credit_percentage_per_signup;
                    baseText = this.$t('referral_benefits.earn_credit_percentage', {
                        referrerText: this.$t('friend_inviter.referrer_text'),
                        percentage,
                    });
                } else {
                    const amount = this.$options.filters.money(this.referralScheme.referred_credit_amount_per_signup, this.referralScheme.currency);
                    baseText = this.$t('friend_inviter.earn_credit_per_signup', { amount });
                }

                if (this.referralScheme.side_of_benefit === 'both') {
                    baseText += this.$t('friend_inviter.both_benefit');
                }

                return baseText;
            }

            // For landing-page or booking-confirmation (original behavior)
            if (!this.referralScheme) {
                return this.$t('friend_inviter.share_event');
            }

            let baseText;
            if (this.referredCreditIsPercentage) {
                const percentage = this.referralScheme.referred_credit_percentage_per_signup;
                baseText = this.$t('friend_inviter.share_and_earn_percentage', { percentage });
            } else {
                const amount = this.$options.filters.money(this.referralScheme.referred_credit_amount_per_signup, this.referralScheme.currency);
                baseText = this.$t('friend_inviter.share_and_earn', { amount });
            }

            if (this.referralScheme.side_of_benefit === 'both') {
                baseText += this.$t('friend_inviter.friend_discount');
            }

            return baseText + this.$t('friend_inviter.start_earning');
        },
        copyConfirmationText() {
            if (this.referralScheme === null) {
                return this.$t('friend_inviter.link_copied');
            }

            if (this.referredCreditIsPercentage) {
                const percentage = this.referralScheme.referred_credit_percentage_per_signup;
                return this.$t('friend_inviter.link_copied_earn_percentage', { percentage });
            }

            const amount = this.$options.filters.money(this.referralScheme.referred_credit_amount_per_signup, this.referralScheme.currency);
            return this.$t('friend_inviter.link_copied_earn', { amount });
        }
    },
    data() {
        return {
            copied: false,
            emails: [],
        }
    },
    methods: {
        onCopy() {
            this.copied = true;
            setInterval(() => {
                this.$root.$emit('bv::hide::modal', 'inviteModal')
                this.copied = false;
            }, 3000)
        }
    }
}
</script>



// WEBPACK FOOTER //
// src/Assets/components/Participants/FriendInviter.vue